import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Map as MapGL, NavigationControl } from "react-map-gl/mapbox";
import { Layer as DeckLayer } from "@deck.gl/core";

import {
  ObservationsMapWidgetConfiguration,
  ParameterTimeSeriesConfiguration,
  ParametersObservedParameter,
} from "@/lib/api";
import { mapRegionPresetNl } from "@/config/map-region-presets";
import { resolveBaseLayerToMapStyleUrl } from "@/lib/mapbox/base-layer-styles";
import { MapStateMachineInput } from "@/lib/map-layers/map-state-machine";
import { useMapLayers } from "@/lib/map-layers";
import { useRefreshAction } from "@/features/dashboard/features/refresh-timer/hooks";
import { usePublishedLocationsQuery } from "@/features/dashboard/features/dashboard/hooks/queries";
import { useSelectedTimeRange } from "@/features/dashboard/store";
import useMapViewState from "@/features/shared/widgets/map/hooks/useMapViewState";
import { useDashboardData } from "@/features/shared/hooks";
import { useDelayedFlag } from "@/features/shared/hooks/use-delayed-flag";
import MapControlPositioner from "@/components/map/map-control-positioner";
import Player from "@/features/shared/widgets/map/controls/player-control";
import { useStaticMapOverlayEntries } from "@/features/shared/widgets/map/static-overlays/use-static-map-overlay-entries";
import { makeTimeSeriesConfigs } from "@/features/shared/widgets/map/time-series/make-time-series-configs";
import { toTimeSeriesParameterOption } from "@/features/shared/widgets/map/time-series/time-series-parameter-option.utils";
import type { TimeSeriesParameterOption } from "@/features/shared/widgets/map/time-series/time-series-parameter.types";
import { StaticOverlayUiById } from "@/features/shared/widgets/model-map/model-map-static-overlays-panel";
import TimeseriesOverlay from "@/features/dashboard/components/timeseries/timeseries-overlay";
import { DeckGLOverlay } from "@/components/deckgl/deckgl-overlay";
import type { ParametersLocation } from "@/lib/api";

import { ObservationsMapLayerControl } from "./observations-map-layer-control";
// Neutralise mapbox-gl Marker._evaluateOpacity so it stops rewriting our marker
// pointer-events; required for decluttered markers to keep the dot clickable
// while the hidden pill footprint stays non-interactive. Side-effect import.
import "./patch-mapbox-marker-opacity";
import { ObservedStationMarkersLayer } from "./observed-station-markers-layer";
import { collectParameterLocationIds } from "./filter-stations-by-dashboard-locations";
import { configuredTimeSeriesParameterIds } from "./hooks/queries";
import { StationTimeseriesPanelContent } from "./station-timeseries-panel";
import { buildObservationsMapLayerConfigs } from "./build-observations-map-layer-configs";
import {
  useObservationsMapResolvedConfig,
  type ObservationsMapResolvedConfig,
} from "./use-observations-map-resolved-config";
import type { MarkerSeriesAggregateStatus } from "./marker-display-state";
import {
  StationMarkersInteractionProvider,
  type StationMarkersInteraction,
} from "./station-markers-interaction-context";
import "@/features/shared/widgets/map/map.css";

/** Default map-body height on the dashboard: fills the widget card minus its
 *  ~76px chrome (header). Hosts without that chrome (e.g. the CMS preview) pass
 *  their own value (e.g. `100cqh`). */
const DASHBOARD_MAP_BODY_HEIGHT = "calc(100cqh - 76px)";

interface ObservationsMapWidgetProps {
  configuration: ObservationsMapWidgetConfiguration;
}

interface ObservationsMapWidgetInnerProps {
  configuration: ObservationsMapWidgetConfiguration;
  resolved: ObservationsMapResolvedConfig;
}

type ObservationsMapViewProps = {
  configuration: ObservationsMapWidgetConfiguration;
  mapLayerInput: MapStateMachineInput;
  timeSeriesConfigs: ReturnType<typeof makeTimeSeriesConfigs>;
  timeSeriesConfigurations: ParameterTimeSeriesConfiguration[];
  configuredParameters: ParametersObservedParameter[];
  hasStationLayer: boolean;
  /**
   * Observed map-layers catalog request is still in flight. Drives a debounced
   * loading indicator in the layer list so slow responses don't look empty.
   */
  isLoadingLayers?: boolean;
  /**
   * Height of the map body box. Defaults to the dashboard card layout; hosts
   * that own the full box (CMS preview) pass e.g. `100cqh`. Resolved against the
   * nearest container-query ancestor, so the host must establish a size query.
   */
  containerHeight?: string;
};

export default function ObservationsMapWrapper({
  configuration,
}: ObservationsMapWidgetProps) {
  const resolved = useObservationsMapResolvedConfig(configuration);
  const {
    hasObservedLayers,
    hasStaticLayers,
    hasStationLayer,
    wantsParameterLayers,
    layersStillResolving,
  } = resolved;

  /* Only defer rendering while parameter layers the widget actually configured are still
   * resolving (prevents remounting the map when the resolved layer set changes from [] to ids).
   * When nothing is configured (empty widget), fall through and render the base map. */
  const awaitingConfiguredLayers =
    !hasObservedLayers &&
    !hasStaticLayers &&
    !hasStationLayer &&
    wantsParameterLayers &&
    layersStillResolving;

  if (awaitingConfiguredLayers) {
    return null;
  }

  return <ObservationsMap configuration={configuration} resolved={resolved} />;
}

export function ObservationsMap({
  configuration,
  resolved,
}: ObservationsMapWidgetInnerProps) {
  const {
    configuredLayers,
    configuredParameters,
    timeSeriesConfigurations,
    displayGroups,
    hasStationLayer,
    layersPending,
  } = resolved;

  const showAllLocations = configuration.show_all_locations ?? true;
  const weatherStationsEnabledByDefault =
    configuration.weather_stations_enabled_by_default ?? true;
  const { selectedDashboard } = useDashboardData();
  // Fetch published (dashboard) locations in BOTH modes: when showAllLocations
  // is true we still use their order to prioritize stations within the full set.
  const publishedLocationsQuery = usePublishedLocationsQuery(selectedDashboard?.id);
  const stationLocationFilterReady = showAllLocations || !publishedLocationsQuery.isPending;

  const linkedParameterLocationIds = useMemo(() => {
    if (showAllLocations) return [];
    return [...collectParameterLocationIds(publishedLocationsQuery.data)].sort();
  }, [showAllLocations, publishedLocationsQuery.data]);

  // Ordered (not sorted) dashboard parameter-location ids drive station
  // prioritization; include in the actor key so the layer re-initializes with
  // the new order once the published locations arrive, in both modes.
  const dashboardLocationOrderKey = useMemo(
    () =>
      (publishedLocationsQuery.data ?? [])
        .map((location) => location.parameter_location_id?.trim())
        .filter((id): id is string => !!id)
        .join(","),
    [publishedLocationsQuery.data],
  );

  const catalogLabel = useCallback(
    (parameterId: string) => configuredParameters.find((p) => p.id === parameterId)?.label,
    [configuredParameters],
  );

  const mapLayerConfigs = useMemo(
    () =>
      buildObservationsMapLayerConfigs({
        configuredLayers,
        hasStationLayer,
        stationLayerReady: stationLocationFilterReady,
        displayGroups,
        timeSeriesConfigurations,
        resolveParameterLabel: catalogLabel,
        showAllLocations,
        weatherStationsEnabledByDefault,
        dashboardLocations: publishedLocationsQuery.data,
      }),
    [
      configuredLayers,
      hasStationLayer,
      stationLocationFilterReady,
      displayGroups,
      timeSeriesConfigurations,
      catalogLabel,
      showAllLocations,
      weatherStationsEnabledByDefault,
      publishedLocationsQuery.data,
    ],
  );

  const timeSeriesConfigs = useMemo(() => {
    // The observed-parameters catalog is only a membership gate inside
    // makeTimeSeriesConfigs — it adds no data to the chart (title, label, unit
    // and visualization type all come from the widget config). When the catalog
    // is unavailable (its metadata endpoint can 500 via an upstream service
    // while the chart *data* endpoints stay healthy), fall back to the parameter
    // ids the config itself references so the charts still render.
    const parameterOptions: TimeSeriesParameterOption[] =
      configuredParameters.length > 0
        ? configuredParameters.map(toTimeSeriesParameterOption)
        : configuredTimeSeriesParameterIds(timeSeriesConfigurations).map((id) => ({
            id,
            label: id,
            units: [],
          }));
    return makeTimeSeriesConfigs(timeSeriesConfigurations, parameterOptions);
  }, [timeSeriesConfigurations, configuredParameters]);

  const timeRange = useSelectedTimeRange();
  const mapLayerInput = useMemo(
    () => ({
      layerConfigs: mapLayerConfigs,
      timeRange,
    }),
    [timeRange, mapLayerConfigs],
  );

  const mapLayerActorKey = useMemo(() => {
    if (mapLayerConfigs.length === 0) return "no-layers";
    const layerIds = mapLayerConfigs.map((config) => config.layer.id).join("|");
    const stationFilterKey = showAllLocations
      ? "all-locations"
      : `linked-${linkedParameterLocationIds.join(",")}`;
    return `${layerIds}|${stationFilterKey}|order-${dashboardLocationOrderKey}`;
  }, [
    mapLayerConfigs,
    showAllLocations,
    linkedParameterLocationIds,
    dashboardLocationOrderKey,
  ]);

  return (
    <ObservationsMapView
      key={mapLayerActorKey}
      configuration={configuration}
      mapLayerInput={mapLayerInput}
      timeSeriesConfigs={timeSeriesConfigs}
      timeSeriesConfigurations={timeSeriesConfigurations}
      configuredParameters={configuredParameters}
      hasStationLayer={hasStationLayer}
      isLoadingLayers={layersPending}
    />
  );
}

/**
 * Dumb observations-map renderer: the map instance plus every in-map control
 * (layers panel, player, legend, static overlays, station time-series panel).
 * All data arrives via props; it fires no catalog/location queries and reads no
 * dashboard store, so both the dashboard smart container and the CMS preview can
 * drive it. Refresh registration is a no-op without a {@link RefreshContext}.
 */
export function ObservationsMapView({
  configuration,
  mapLayerInput,
  timeSeriesConfigs,
  timeSeriesConfigurations,
  configuredParameters,
  hasStationLayer,
  isLoadingLayers = false,
  containerHeight = DASHBOARD_MAP_BODY_HEIGHT,
}: ObservationsMapViewProps) {
  const mapLayers = useMapLayers(mapLayerInput);

  // Defer the layer-list placeholders so fast responses don't flash them.
  const showLayersLoading = useDelayedFlag(isLoadingLayers, 600);
  // One ghost row per configured observed layer we are still waiting for.
  const ghostRowCount = showLayersLoading
    ? (configuration.parameter_map_layer_configurations?.length ?? 0)
    : 0;

  const staticOverlayConfigs = useMemo(
    () => configuration.static_map_overlay_configurations,
    [configuration.static_map_overlay_configurations],
  );
  const staticOverlayEntries = useStaticMapOverlayEntries(staticOverlayConfigs);

  const [staticOverlayUiById, setStaticOverlayUiById] = useState<StaticOverlayUiById>({});
  const [expandedTimeseries, setExpandedTimeseries] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<
    ParametersLocation | undefined
  >();
  const [stationLayerAggregateStatus, setStationLayerAggregateStatus] =
    useState<MarkerSeriesAggregateStatus>({
      anyPending: false,
      anyError: false,
    });

  const handleSelectLocation = useCallback((location: ParametersLocation) => {
    setSelectedLocation((current) => (current?.id === location.id ? undefined : location));
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedLocation(undefined);
  }, []);

  const handleStationLayerAggregateStatusChange = useCallback(
    (status: MarkerSeriesAggregateStatus) => {
      setStationLayerAggregateStatus((previous) =>
        previous.anyPending === status.anyPending && previous.anyError === status.anyError
          ? previous
          : status,
      );
    },
    [],
  );

  const stationMarkersInteraction = useMemo(
    (): StationMarkersInteraction => ({
      selectedLocationId: selectedLocation?.id,
      onSelectLocation: handleSelectLocation,
      onClearSelection: handleClearSelection,
      timeSeriesConfigurations,
      configuredParameters,
      onAggregateStatusChange: handleStationLayerAggregateStatusChange,
    }),
    [
      selectedLocation?.id,
      handleSelectLocation,
      handleClearSelection,
      timeSeriesConfigurations,
      configuredParameters,
      handleStationLayerAggregateStatusChange,
    ],
  );

  useEffect(() => {
    if (!hasStationLayer) {
      setSelectedLocation(undefined);
      setExpandedTimeseries(false);
      setStationLayerAggregateStatus({ anyPending: false, anyError: false });
    }
  }, [hasStationLayer]);

  useEffect(() => {
    if (staticOverlayEntries.length === 0) {
      setStaticOverlayUiById((prev) => (Object.keys(prev).length > 0 ? {} : prev));
      return;
    }
    setStaticOverlayUiById((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const e of staticOverlayEntries) {
        if (next[e.instanceId] === undefined) {
          next[e.instanceId] = { visible: e.enabledByDefault };
          changed = true;
        }
      }
      for (const k of Object.keys(next)) {
        if (!staticOverlayEntries.some((e) => e.instanceId === k)) {
          delete next[k];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [staticOverlayEntries]);

  const visibleStaticDeckLayers = useMemo((): DeckLayer[] => {
    const layers: DeckLayer[] = [];
    for (const e of staticOverlayEntries) {
      const visible = staticOverlayUiById[e.instanceId]?.visible ?? e.enabledByDefault;
      if (e.layer != null) layers.push(e.layer.clone({ visible }));
    }
    return layers;
  }, [staticOverlayEntries, staticOverlayUiById]);

  const setStaticOverlayVisible = useCallback((instanceId: string, visible: boolean) => {
    setStaticOverlayUiById((prev) => ({
      ...prev,
      [instanceId]: { ...prev[instanceId], visible },
    }));
  }, []);

  useRefreshAction(mapLayers.refresh);

  useEffect(() => {
    if (!hasStationLayer) return;

    for (const config of mapLayerInput.layerConfigs) {
      if (config.layer instanceof ObservedStationMarkersLayer) {
        config.layer.setOnDimensionsChanged(() => mapLayers.recalculateDimensions());
      }
    }
  }, [hasStationLayer, mapLayerInput.layerConfigs, mapLayers.recalculateDimensions]);

  const initialCoords = useMemo(() => {
    const b = configuration.initial_viewport_bounds;
    if (b) {
      return [
        { lat: b.south_latitude, lng: b.west_longitude },
        { lat: b.north_latitude, lng: b.east_longitude },
      ];
    }
    return mapRegionPresetNl.toBaseLayerCoords();
  }, [configuration.initial_viewport_bounds]);

  const mapStyleUrl = useMemo(
    () => resolveBaseLayerToMapStyleUrl(configuration.base_layer_style),
    [configuration.base_layer_style],
  );

  const mapTileAccessToken = import.meta.env.VITE_MAP_ACCESS_TOKEN;
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const { mapViewState } = useMapViewState({
    containerRef: mapContainerRef,
    initialCoords,
  });

  const shouldShowLayerControl = useMemo(() => {
    return !selectedLocation || !expandedTimeseries;
  }, [expandedTimeseries, selectedLocation]);

  const staticOverlayRowCount = staticOverlayConfigs.length;
  const hasLayerControlTargets =
    mapLayers.layers.length > 0 || staticOverlayRowCount > 0 || ghostRowCount > 0;

  const body = (
    <StationMarkersInteractionProvider value={stationMarkersInteraction}>
      <div>
        <div
          style={{ height: containerHeight, position: "relative" }}
          ref={mapContainerRef}
        >
          {mapViewState && (
            <MapGL
              initialViewState={mapViewState}
              attributionControl={false}
              maxPitch={0}
              bearing={0}
              bearingSnap={360}
              mapboxAccessToken={mapTileAccessToken}
              mapStyle={mapStyleUrl}
              projection={{ name: "mercator" }}
            >
              <DeckGLOverlay
                controller
                interleaved
                getCursor={({ isDragging }) => (isDragging ? "grabbing" : "grab")}
                layers={[
                  ...mapLayers.layers
                    .map((it) => it.renderedLayer)
                    .filter((layer): layer is NonNullable<typeof layer> => layer != null),
                  ...visibleStaticDeckLayers,
                ]}
              />

              {mapLayers.layers.map((it) => (
                <React.Fragment key={it.id}>{it.renderedComponents}</React.Fragment>
              ))}

              <NavigationControl showCompass={false} position="bottom-right" showZoom />

              <Player {...mapLayers} />

              {shouldShowLayerControl && hasLayerControlTargets && (
                <MapControlPositioner position="top-left">
                  <ObservationsMapLayerControl
                    mapLayers={mapLayers}
                    staticOverlayEntries={staticOverlayEntries}
                    staticOverlayUiById={staticOverlayUiById}
                    onSetStaticOverlayVisible={setStaticOverlayVisible}
                    stationLayerAggregateStatus={
                      hasStationLayer ? stationLayerAggregateStatus : undefined
                    }
                    ghostRowCount={ghostRowCount}
                  />
                </MapControlPositioner>
              )}

              {selectedLocation && timeSeriesConfigs.length > 0 && (
                <MapControlPositioner position="top-right">
                  <TimeseriesOverlay
                    expanded={expandedTimeseries}
                    setExpanded={setExpandedTimeseries}
                    headerTitle={selectedLocation.name}
                    onClose={handleClearSelection}
                  >
                    <StationTimeseriesPanelContent
                      location={selectedLocation}
                      timeSeriesConfigs={timeSeriesConfigs}
                      timeSeriesConfigurations={timeSeriesConfigurations}
                      // bug: time may be undefined.
                      currentTime={mapLayers.currentDimensionValues["time"]}
                    />
                  </TimeseriesOverlay>
                </MapControlPositioner>
              )}
            </MapGL>
          )}
        </div>
      </div>
    </StationMarkersInteractionProvider>
  );

  // Timezone (and every other widget-runtime input) comes from the ambient
  // widget runtime, which a host wrapper — the dashboard page or the CMS
  // WidgetContextHost — must establish around this view.
  return body;
}
